import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Area, type Me } from "@/lib/api";
import { passkeySignIn } from "@/lib/webauthn";

type AuthValue = {
  me: Me | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<Me>;
  /**
   * Die Anmeldung ohne Passwort: der Passkey ist beides, Besitz und
   * Entsperren, also schuldet die Sitzung danach keinen zweiten Faktor.
   */
  signInWithPasskey: () => Promise<Me>;
  signOut: () => Promise<void>;
  setScope: (companyId: number) => Promise<void>;
  /**
   * Replaces what we know about ourselves, which the second-factor screens do
   * the moment the session stops being half-finished.
   */
  setMe: (me: Me) => void;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: () => api<Me>("/api/admin/me"),
    // Not being signed in is an ordinary state, not a failure worth retrying.
    retry: (_count, error) =>
      !(error instanceof ApiError && error.isUnauthenticated),
    staleTime: 30_000,
  });

  const signIn = useMutation({
    mutationFn: (vars: { email: string; password: string }) =>
      api<Me>("/api/admin/session", { method: "POST", body: vars }),
    onSuccess: (me) => qc.setQueryData(["me"], me),
  });

  const signInWithPasskey = useMutation({
    mutationFn: () => passkeySignIn(),
    onSuccess: (me) => qc.setQueryData(["me"], me),
  });

  const signOut = useMutation({
    mutationFn: () => api<void>("/api/admin/session", { method: "DELETE" }),
    onSuccess: () => {
      qc.setQueryData(["me"], null);
      qc.clear();
    },
  });

  const setScope = useMutation({
    mutationFn: (companyId: number) =>
      api<Me>("/api/admin/me/scope", { method: "PUT", body: { companyId } }),
    onSuccess: (me) => {
      qc.setQueryData(["me"], me);
      // Everything else is scoped to a company, so it all has to be refetched.
      void qc.invalidateQueries();
    },
  });

  const value = useMemo<AuthValue>(
    () => ({
      me: data ?? null,
      loading: isLoading,
      signIn: async (email, password) => signIn.mutateAsync({ email, password }),
      signInWithPasskey: async () => signInWithPasskey.mutateAsync(),
      signOut: async () => {
        await signOut.mutateAsync();
      },
      setScope: async (companyId) => {
        await setScope.mutateAsync(companyId);
      },
      setMe: (next) => qc.setQueryData(["me"], next),
    }),
    [data, isLoading, signIn, signInWithPasskey, signOut, setScope, qc],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/** The company whose data the interface is currently showing. */
export function useScope(): number {
  const { me } = useAuth();
  return me?.scopeCompanyId || (me?.role === "global_admin" ? 0 : (me?.companyId ?? 0));
}

/**
 * Ob der angemeldete Verwalter einen der eingeschränkten Bereiche betreten
 * darf. Die Liste kommt vom Server, damit ein Menüeintrag nicht auf eine Seite
 * führt, die nur Absagen anzeigt.
 */
export function useMayUse(area: Area): boolean {
  const { me } = useAuth();
  return me?.areas?.includes(area) ?? false;
}

/** Refetch everything after a change that could affect several views. */
export function useRefreshAll() {
  const qc = useQueryClient();
  return useCallback(() => void qc.invalidateQueries(), [qc]);
}
